import {
	TAdminJobs,
	TAdminPlanChanges,
	TAdminPlanImpact,
	TAdminMetrics,
	TAdminPlanList,
	TAdminReconcileResult,
	TAdminReplayResult,
	TAdminStripeEventList,
	TAdminWorkspaceDetail,
	TAdminUserList,
	TAdminFeatureFlag,
} from "@/types/TAdmin";
import { TPlanCode, TSubscription } from "@/types/TSubscription";
import { TFeatureFlag } from "@/types/TUser";

import API from "./api";

/** Admin only (ROLE_ADMIN): the API answers 403 to anyone else. */
class AdminServiceClass {
	/** The accounts, newest first. `q` searches name, username and mail. */
	users = (q: string, page: number, limit = 25): Promise<TAdminUserList> => {
		return API.get("admin/users", {
			params: { q: q || undefined, page, limit },
		}).then((res) => res.data);
	};

	workspace = (id: number): Promise<TAdminWorkspaceDetail> => {
		return API.get(`admin/workspaces/${id}`).then((res) => res.data);
	};

	/**
	 * Grants a plan by hand, with nothing at Stripe behind it. The API
	 * refuses (409) while a Stripe subscription is live on the account.
	 */
	setPlan = (
		workspaceId: number,
		plan: TPlanCode
	): Promise<TSubscription> => {
		return API.patch(`admin/workspaces/${workspaceId}/plan`, { plan }).then(
			(res) => res.data
		);
	};

	/** Adds days to the local trial, from the later of now and its end. 409 with a Stripe subscription behind. */
	extendTrial = (
		workspaceId: number,
		days: number
	): Promise<TSubscription> => {
		return API.patch(`admin/workspaces/${workspaceId}/trial`, {
			days,
		}).then((res) => res.data);
	};

	/** Re-reads the account's Stripe subscription. 409 without one. */
	reconcileWorkspace = (workspaceId: number): Promise<TSubscription> => {
		return API.post(`admin/workspaces/${workspaceId}/reconcile`).then(
			(res) => res.data
		);
	};

	/** Every plan next to Stripe's catalogue, verdict per sold price. */
	/** What exists for whom (feature_flags). */
	features = (): Promise<TAdminFeatureFlag[]> => {
		return API.get("admin/features").then((res) => res.data);
	};

	setFeature = (
		code: TFeatureFlag,
		changes: { enabled: boolean; enabledForRoles: string[] }
	): Promise<TAdminFeatureFlag> => {
		return API.patch(`admin/features/${code}`, changes).then(
			(res) => res.data
		);
	};

	plans = (): Promise<TAdminPlanList> => {
		return API.get("admin/plans").then((res) => res.data);
	};

	/** Copies Stripe's ids and amounts over ours, by lookup key. 409 with `details.missing` if a key is absent. */
	syncPlans = (): Promise<TAdminPlanList> => {
		return API.post("admin/plans/sync").then((res) => res.data);
	};

	/** Limits (null = unlimited) and flags. `dryRun` only answers the impact. */
	updatePlan = (
		id: number,
		changes: TAdminPlanChanges,
		dryRun = false
	): Promise<TAdminPlanImpact> => {
		return API.patch(`admin/plans/${id}`, changes, {
			params: dryRun ? { dryRun: 1 } : undefined,
		}).then((res) => res.data);
	};

	stripeEvents = (limit = 50): Promise<TAdminStripeEventList> => {
		return API.get("admin/stripe-events", { params: { limit } }).then(
			(res) => res.data
		);
	};

	/** Runs a recorded delivery again from its stored payload. 409 if it fails again. */
	replayEvent = (id: string): Promise<TAdminReplayResult> => {
		return API.post(`admin/stripe-events/${id}/replay`).then(
			(res) => res.data
		);
	};

	/** What `billing:reconcile` does nightly. */
	reconcileAll = (): Promise<TAdminReconcileResult> => {
		return API.post("admin/billing/reconcile").then((res) => res.data);
	};

	/** The scheduled jobs: last run, last success, stale or not; and the latest runs. */
	jobs = (): Promise<TAdminJobs> => {
		return API.get("admin/jobs").then((res) => res.data);
	};

	metrics = (): Promise<TAdminMetrics> => {
		return API.get("admin/metrics").then((res) => res.data);
	};
}

const AdminService = new AdminServiceClass();

export default AdminService;
